import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { LuZap, LuCar, LuHistory } from 'react-icons/lu';
import Page from '../../components/ui/Page/Page';
import SectionHeader from '../../components/ui/SectionHeader/SectionHeader';
import Card from '../../components/ui/Card/Card';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import Button from '../../components/ui/Button/Button';
import { useAuth } from '../../context/AuthContext';
import { useSession } from '../../context/SessionContext';
import { useUserVehicles } from '../../hooks/useUserVehicles';
import { useAccountSessions } from '../../hooks/useAccountSessions';
import ErrorState from '../../components/ui/ErrorState/ErrorState';
import { SkeletonCard } from '../../components/ui/LoadingSkeleton/LoadingSkeleton';
import ActiveSessionCard from '../../components/domain/ActiveSessionCard/ActiveSessionCard';
import VehicleCard from '../../components/domain/VehicleCard/VehicleCard';
import ChargingSessionCard from '../../components/domain/ChargingSessionCard/ChargingSessionCard';
import InsightsSummaryCard from '../../components/domain/InsightsSummaryCard/InsightsSummaryCard';
import './DashboardPage.less';

// How many completed sessions to surface in "Recent activity". The
// Charging History page owns the full paginated list; this is the
// quick-glance top of the pile.
const RECENT_ACTIVITY_LIMIT = 3;

function timeGreeting(name) {
  const h = new Date().getHours();
  const salutation = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  return name ? `${salutation}, ${name}` : salutation;
}

function DashboardPage() {
  const { user } = useAuth();
  const session = useSession();
  const activeSession = session?.state?.sessionUid ? session.state : null;

  const {
    vehicles,
    primary,
    isLoading: vehiclesLoading,
    error: vehiclesError,
    refresh: refreshVehicles,
  } = useUserVehicles();

  // Recent activity reads the SAME canonical, account-scoped API that
  // powers /history — the previous localStorage-only source silently
  // showed the empty state for users whose sessions were completed on
  // another device (or before local-history recording landed). See the
  // Charging History page for the full paginated view; we only need
  // the 3 newest here.
  const {
    sessions: recentSessions,
    total: recentTotal,
    loading: recentLoading,
    error: recentError,
    refresh: refreshRecent,
  } = useAccountSessions({ pageSize: RECENT_ACTIVITY_LIMIT });

  // Refetch Recent activity once a live session finishes. Without
  // this, a user who just completed a charge would keep seeing a
  // stale list (or an empty state) until they hard-refreshed. We
  // watch for the sessionUid transition from truthy -> null which
  // is how SessionContext signals "the wizard cleared its live
  // session".
  const activeUidRef = useRef(activeSession?.sessionUid || null);
  useEffect(() => {
    const nextUid = activeSession?.sessionUid || null;
    if (activeUidRef.current && !nextUid) {
      refreshRecent();
    }
    activeUidRef.current = nextUid;
  }, [activeSession, refreshRecent]);

  const firstName = user?.firstName?.trim() || '';
  const hasVehicles = vehicles.length > 0;
  // The empty state is only shown after a successful fetch that
  // returned zero — never during the initial load or when the
  // request failed. This is the fix for the false "No vehicles yet"
  // banner the audit surfaced.
  const showVehiclesEmpty = !vehiclesLoading && !vehiclesError && !hasVehicles;

  // Same rule for Recent activity: don't announce "no sessions" until
  // we've heard back from the server and it genuinely said zero.
  const hasHistory = recentSessions.length > 0;
  const recentIsInitialLoad = recentLoading && recentSessions.length === 0 && !recentError;
  const showRecentEmpty =
    !recentLoading && !recentError && recentTotal === 0 && recentSessions.length === 0;

  return (
    <Page className="gc-dashboard-page">
      {/* Hero — the visual anchor. When a session is live, the
          ActiveSessionCard takes the anchor spot; otherwise a
          tone="hero" Card carries the greeting eyebrow + primary
          question + brand CTA. */}
      {activeSession ? (
        <section className="gc-dashboard-hero-live" aria-labelledby="dash-hero-title">
          <h1 id="dash-hero-title" className="gc-visually-hidden">Charging in progress</h1>
          <p className="gc-dashboard-hero-eyebrow">{timeGreeting(firstName)}</p>
          <ActiveSessionCard session={activeSession} />
        </section>
      ) : (
        <Card
          as="section"
          tone="hero"
          className="gc-dashboard-hero"
          aria-labelledby="dash-hero-title"
        >
          <p className="gc-dashboard-hero-eyebrow">{timeGreeting(firstName)}</p>
          <h1 id="dash-hero-title" className="gc-dashboard-hero-title">
            Ready to charge?
          </h1>
          <p className="gc-dashboard-hero-sub">
            Scan a charger or find one nearby.
          </p>
          <div className="gc-dashboard-hero-actions">
            <Button
              as={Link}
              to="/charge"
              size="lg"
              leadingIcon={<LuZap />}
            >
              Start charging
            </Button>
          </div>
        </Card>
      )}

      <section
        aria-labelledby="dash-vehicles"
        className="gc-dashboard-vehicles-section"
      >
        <SectionHeader
          title={<span id="dash-vehicles">Your vehicles</span>}
          action={hasVehicles && <Link to="/vehicles">Manage</Link>}
        />

        {vehiclesLoading && !hasVehicles && (
          <div className="gc-dashboard-vehicles" aria-busy="true" aria-live="polite">
            <SkeletonCard />
          </div>
        )}

        {vehiclesError && (
          <ErrorState
            title="Could not load your vehicles"
            description={vehiclesError}
            onRetry={refreshVehicles}
          />
        )}

        {showVehiclesEmpty && (
          <Card>
            <EmptyState
              icon={<LuCar />}
              title="No vehicles yet"
              description="Vehicles on your GreenCharge account will appear here. If you have already registered one, contact support to check the details on file."
            />
          </Card>
        )}

        {hasVehicles && (
          <div className="gc-dashboard-vehicles">
            {[primary, ...vehicles.filter((v) => v !== primary)]
              .filter(Boolean)
              .slice(0, 2)
              .map((v) => (
                <VehicleCard
                  key={v.id || v.plateNormalized}
                  vehicle={v}
                  to={`/vehicles/${encodeURIComponent(v.plateNormalized)}`}
                />
              ))
            }
          </div>
        )}
      </section>

      <section aria-labelledby="dash-insights" className="gc-dashboard-insights-section">
        <SectionHeader
          title={<span id="dash-insights">Energy</span>}
        />
        <InsightsSummaryCard />
      </section>

      <section aria-labelledby="dash-recent">
        <SectionHeader
          title={<span id="dash-recent">Recent activity</span>}
          action={hasHistory && <Link to="/history">View all</Link>}
        />

        {recentIsInitialLoad && (
          <div className="gc-dashboard-history" aria-busy="true" aria-live="polite">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        )}

        {!recentIsInitialLoad && recentError && (
          <ErrorState
            title="Could not load your recent activity"
            description="Please check your connection and try again."
            onRetry={refreshRecent}
          />
        )}

        {showRecentEmpty && (
          <Card>
            <EmptyState
              icon={<LuHistory />}
              title="No charging sessions yet"
              description="Your charging history will appear here after your first session."
            />
          </Card>
        )}

        {hasHistory && (
          <div className="gc-dashboard-history">
            {recentSessions.slice(0, RECENT_ACTIVITY_LIMIT).map((s) => (
              <ChargingSessionCard
                key={s.sessionUid}
                session={s}
                to={`/history/${encodeURIComponent(s.sessionUid)}`}
              />
            ))}
          </div>
        )}
      </section>
    </Page>
  );
}

export default DashboardPage;
